// Loan File Detail — top-level page shell.
//
// Refactored Feb 3, 2026: this file was 1,852 lines of module UI. All 19
// module components have moved to `/portal/loan-file/modules/*.jsx`; the
// header, sidebar, and shared form primitives live in
// `/portal/loan-file/shared/*`. This page now only owns data fetching,
// status advancement, and routing between modules.
import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { losApi, LOAN_STATUS_LABELS, LOAN_STATUS_ORDER } from './losApi';
import { Loader2, CheckSquare, Layers } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { confirmDialog, promptDialog } from '@/components/dialogs/DialogHost';
import { toastAriveConflict } from './ariveManaged';
import { errorMessage } from './errors';
import UrlaFullEditor from './UrlaFullEditor';

// Loan file shell — extracted pieces
import { LoanHeader } from './loan-file/shared/LoanHeader';
import { LoanSidebar, LoanSectionPicker } from './loan-file/shared/LoanSidebar';
import { PostClosingCard } from './loan-file/shared/PostClosingCard';

// Module screens
import { LoanPropertyModule } from './loan-file/modules/LoanPropertyModule';
import { BorrowerInfoModule } from './loan-file/modules/BorrowerInfoModule';
import { FinancialInfoModule } from './loan-file/modules/FinancialInfoModule';
import { ProductsPricingModule } from './loan-file/modules/ProductsPricingModule';
import { PreApprovalModule } from './loan-file/modules/PreApprovalModule';
import { LoanCenterModule } from './loan-file/modules/LoanCenterModule';
import { ClientNeedsModule } from './loan-file/modules/ClientNeedsModule';
import { DocumentsModule } from './loan-file/modules/DocumentsModule';
import { DisclosureFormsModule } from './loan-file/modules/DisclosureFormsModule';
import { FundingRevenueModule } from './loan-file/modules/FundingRevenueModule';
import { AuditDatesModule } from './loan-file/modules/AuditDatesModule';
import { ConditionsTab } from './loan-file/modules/ConditionsTab';
import { RateLockTab } from './loan-file/modules/RateLockTab';
import { LeFeesTab } from './loan-file/modules/LeFeesTab';
import { PartiesTab } from './loan-file/modules/PartiesTab';
import { AiProcessorTab } from './loan-file/modules/AiProcessorTab';
import { PlaceholderModule } from './loan-file/modules/PlaceholderModule';
import { AriveModule } from './loan-file/modules/AriveModule';

export default function LoanFileDetailPage() {
  const { loanId } = useParams();
  const [params, setParams] = useSearchParams();
  const [loan, setLoan] = useState(null);
  const [saving, setSaving] = useState(false);
  // `?section=documents` deep-links to a module (the unified deal page uses it). The URL is
  // the state: picking a module rewrites it, so a copied link or a reload lands on the same tab.
  const section = params.get('section') || 'loan_property';
  const setSection = (key) => setParams((prev) => {
    const next = new URLSearchParams(prev);
    next.set('section', key);
    return next;
  }, { replace: true });

  // Set when the first load fails; a failed refresh after a save keeps the loan on screen.
  const [loadError, setLoadError] = useState(null);

  const reload = () => losApi.getLoan(loanId).then((l) => { setLoan(l); setLoadError(null); });
  const load = () => {
    setLoadError(null);
    return reload().catch((e) => setLoadError(e));
  };
  useEffect(() => { setLoan(null); load(); }, [loanId]);

  // Any move is allowed but one. Moving to Processing or later with checks still open asks
  // once, listing them; a clean file, an earlier stage or a failed lookup moves without
  // asking. The exception (D28): Closing or Funded with the CD fewer than three business
  // days before consummation is held; an owner or admin may go ahead with a typed reason.
  const openChecks = async (status) => {
    if (LOAN_STATUS_ORDER.indexOf(status) < LOAN_STATUS_ORDER.indexOf('processing')) return { open: [] };
    try {
      const r = await losApi.loanChecks(loanId);
      return { open: (r.findings || []).filter((f) => f.status !== 'pass'), canOverride: !!r.can_override_trid };
    } catch {
      return { open: [] };
    }
  };

  const openList = (open) => (
    <>
      {open.length} compliance check{open.length === 1 ? ' is' : 's are'} still open:
      {open.map((f) => (
        <span key={f.rule || f.label} className="block mt-1" data-testid="open-check">
          • {f.label}{f.status === 'fail' ? ' (failing)' : ''}
        </span>
      ))}
    </>
  );

  const holdMessage = (f) => `The Closing Disclosure needs 3 business days before closing: ${f.detail}.`;

  // Resolves to the typed reason, or null when cancelled.
  const askOverrideReason = (status, message, open = []) => promptDialog({
    title: `Override the TRID hold and move to ${LOAN_STATUS_LABELS[status] || status}?`,
    description: (
      <>
        <span className="block mb-2">{message}</span>
        {open.length > 0 && openList(open)}
      </>
    ),
    label: 'Reason (kept in the audit trail)',
    placeholder: 'Why this loan closes inside the waiting period',
    multiline: true,
    confirmText: 'Override and move',
  });

  const move = async (status, extra = {}) => {
    const updated = await losApi.updateLoan(loanId, { status, ...extra });
    setLoan(updated);
    toast({ title: 'Status updated', description: LOAN_STATUS_LABELS[status] });
  };

  const advanceStatus = async (status) => {
    if (status === loan.status) return;
    setSaving(true);
    try {
      const { open, canOverride } = await openChecks(status);
      const hold = open.find((f) => f.status === 'fail' && (f.blocks || []).includes(status));
      if (hold) {
        if (!canOverride) {
          toast({ title: 'TRID hold', description: `${holdMessage(hold)} An owner or admin can override it.` });
          return;
        }
        const reason = await askOverrideReason(status, holdMessage(hold), open);
        if (!reason) return;
        await move(status, { trid_override_reason: reason });
        return;
      }
      if (open.length) {
        const ok = await confirmDialog({
          title: `Move to ${LOAN_STATUS_LABELS[status] || status}?`,
          description: openList(open),
          confirmText: 'Move anyway',
        });
        if (!ok) return;
      }
      try {
        await move(status);
      } catch (e) {
        // The checks lookup failed or the dates moved since: the server still holds it.
        const d = e.response?.data?.detail;
        if (e.response?.status !== 409 || d?.code !== 'trid_cd_waiting_period' || !d.can_override) throw e;
        const reason = await askOverrideReason(status, d.message);
        if (!reason) return;
        await move(status, { trid_override_reason: reason });
      }
    } catch (e) {
      if (!toastAriveConflict(e)) toast({ title: 'Failed', description: errorMessage(e, 'Error') });
    }
    finally { setSaving(false); }
  };

  if (loadError && !loan) return <LoadFailure error={loadError} onRetry={load} />;
  if (!loan) return <div className="p-10 text-slate-500 flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Loading…</div>;

  return (
    <div style={{ background: '#F5F6FA', minHeight: 'calc(100vh - 56px)' }}>
      <LoanHeader loan={loan} onStatus={advanceStatus} saving={saving} onOpenSection={setSection} />

      {/* Sidebar + main content */}
      <div className="max-w-[1600px] mx-auto px-2 md:px-4 py-4">
        <div className="flex gap-4">
          <LoanSidebar loan={loan} section={section} setSection={setSection} />
          <div className="flex-1 min-w-0">
            <LoanSectionPicker section={section} setSection={setSection} loan={loan} />
            <PostClosingCard loan={loan} onChange={reload} />
            {/* An archived loan is read-only: the server refuses writes, this stops the attempts. */}
            <fieldset disabled={!!loan.archived_at} className="min-w-0 border-0 p-0 m-0" data-testid="loan-modules">
              <ModuleContent loan={loan} section={section} reload={reload} />
            </fieldset>
          </div>
        </div>
      </div>
    </div>
  );
}

function LoadFailure({ error, onRetry }) {
  const status = error?.response?.status;
  const message = status === 404 ? 'This loan file does not exist, or was moved.'
    : status === 403 ? 'You do not have access to this loan file.'
    : 'The loan file could not be loaded. Check your connection and try again.';
  return (
    <div className="p-10 max-w-lg" data-testid="loan-load-failure">
      <div className="text-[15px] font-semibold mb-1" style={{ color: '#030524' }}>Could not open this loan</div>
      <div className="text-[13px] text-slate-600 mb-4">{message}</div>
      <div className="flex gap-2">
        {status !== 404 && status !== 403 && (
          <button type="button" onClick={onRetry} data-testid="loan-load-retry"
            className="h-9 px-4 rounded-md text-[13px] font-semibold text-white" style={{ background: '#030524' }}>Retry</button>
        )}
        <Link to="/portal/loans" className="h-9 px-4 inline-flex items-center rounded-md text-[13px] font-semibold border border-slate-300 text-slate-700">Back to loans</Link>
      </div>
    </div>
  );
}

function ModuleContent({ loan, section, reload }) {
  switch (section) {
    case 'loan_property':     return <LoanPropertyModule loan={loan} onChange={reload} />;
    case 'borrower_info':     return <BorrowerInfoModule loan={loan} onChange={reload} />;
    case 'financial_info':    return <FinancialInfoModule loan={loan} onChange={reload} />;
    case 'products_pricing':  return <ProductsPricingModule loan={loan} onChange={reload} />;
    case 'review_fees':       return <LeFeesTab loan={loan} onChange={reload} />;
    case 'dual_aus':          return <PlaceholderModule title="Dual AUS" subtitle="DU / LP / TOTAL Scorecard results will render here once you order findings from the AUS integrations." icon={CheckSquare} />;
    case 'pre_approval':      return <PreApprovalModule loan={loan} />;
    case 'loan_center':       return <LoanCenterModule loan={loan} onChange={reload} />;
    case 'client_needs':      return <ClientNeedsModule loan={loan} onChange={reload} />;
    case 'conditions':        return <ConditionsTab loan={loan} onChange={reload} />;
    case 'documents':         return <DocumentsModule loan={loan} onChange={reload} />;
    case 'disclosure_forms':  return <DisclosureFormsModule loan={loan} />;
    case 'loan_quotes':       return <PlaceholderModule title="Loan Quotes" subtitle="Side-by-side quote comparison from your lender panel — coming next." icon={Layers} />;
    case 'funding_revenue':   return <FundingRevenueModule loan={loan} />;
    case 'audit_dates':       return <AuditDatesModule loan={loan} onChange={reload} />;
    case 'urla_full':         return <UrlaFullEditor loan={loan} onSaved={reload} />;
    case 'parties_sharing':   return <PartiesTab loan={loan} onChange={reload} />;
    case 'ai_processor':      return <AiProcessorTab loan={loan} onChange={reload} />;
    case 'arive':             return <AriveModule loan={loan} />;
    default:                  return <LoanPropertyModule loan={loan} onChange={reload} />;
  }
}
